import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigationHealthService, NavigationContext } from '../services/navigationHealthService';
import { recoveryService } from '../services/recoveryService';
import { smartCacheManager } from '../services/smartCacheManager';
import { performanceMonitor } from '../services/performanceMonitor';
import { userExperienceTracker } from '../services/userExperienceTracker';
import { intelligentRetryService } from '../services/intelligentRetryService';
import { backgroundSyncService } from '../services/backgroundSyncService';
import { predictivePreloader } from '../services/predictivePreloader';

interface NavigationHealthOptions {
  enableHealthCheck?: boolean;
  enableAutoRecovery?: boolean;
  performanceThreshold?: number;
}

export const useNavigationHealth = (options: NavigationHealthOptions = {}) => {
  const {
    enableHealthCheck = true,
    enableAutoRecovery = true,
    performanceThreshold = 50
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);
  const lastNavigationRef = useRef<string>('');

  // Healthy navigation function
  const healthyNavigate = useCallback(async (
    to: string,
    options?: { replace?: boolean; state?: any },
    type: NavigationContext['type'] = 'programmatic'
  ) => {
    if (isNavigatingRef.current) return;
    
    isNavigatingRef.current = true;
    const startTime = performance.now();

    try {
      const context: NavigationContext = {
        from: location.pathname,
        to,
        type,
        timestamp: Date.now()
      };

      // Pre-navigation health check with intelligent retry
      if (enableHealthCheck) {
        const isHealthy = await intelligentRetryService.executeWithRetry(
          () => navigationHealthService.preNavigationCheck(context),
          `health_check_${to}`,
          { maxAttempts: 2, baseDelay: 500 }
        );
        
        if (!isHealthy) {
          if (enableAutoRecovery) {
            const recovered = await recoveryService.attemptAutoRecovery({
              message: 'Navigation health check failed',
              context
            });

            if (!recovered) {
              await recoveryService.notifySuperAdmin(
                { message: 'Navigation health check failed, auto recovery unsuccessful' },
                context
              );
              throw new Error('Sistem sağlık kontrolü başarısız');
            }
          } else {
            throw new Error('Sistem sağlık kontrolü başarısız');
          }
        }
      }

      // Enhanced cache management with background sync
      const cacheKey = `page_${to}`;
      const cachedData = smartCacheManager.getPageCache(cacheKey);
      
      if (!cachedData) {
        // Warm cache for new route
        await smartCacheManager.warmCache([to]);
        
        // Schedule background data preloading
        backgroundSyncService.addTask('cache_update', {
          action: 'warm',
          key: cacheKey,
          route: to
        }, 'medium');
      }

      // Perform navigation with retry
      await intelligentRetryService.executeWithRetry(
        async () => {
          navigate(to, options);
          return Promise.resolve();
        },
        `navigation_${to}`,
        { maxAttempts: 2, baseDelay: 100 }
      );
      
      lastNavigationRef.current = to;

      // Performance monitoring
      const navigationTime = performance.now() - startTime;
      performanceMonitor.recordMetric('navigation', navigationTime, {
        from: location.pathname,
        to,
        type
      });
      
      // UX tracking
      userExperienceTracker.recordNavigation(to);
      
      if (navigationTime > performanceThreshold) {
        // Only log in development
        if (import.meta.env.DEV) {
          console.warn(`Slow navigation detected: ${navigationTime}ms to ${to}`);
        }
      }

    } catch (error) {
      console.error('Navigation failed:', error);
      
      // Record navigation failure
      performanceMonitor.recordMetric('navigation', -1, {
        error: error.message,
        from: location.pathname,
        to,
        type
      });
      
      if (enableAutoRecovery) {
        const recovered = await recoveryService.attemptAutoRecovery(error);
        if (!recovered) {
          // Show user recovery options
          const actions = recoveryService.getUserRecoveryActions(error);
          // This would trigger a modal or notification with recovery actions
          console.log('Recovery actions available:', actions);
        }
      }

      await recoveryService.notifySuperAdmin(error, {
        location: location.pathname,
        target: to,
        type
      });

      throw error;
    } finally {
      isNavigatingRef.current = false;
    }
  }, [navigate, location, enableHealthCheck, enableAutoRecovery, performanceThreshold]);

  // Intercept click events for sidebar and buttons
  useEffect(() => {
    const handleClick = async (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:')) return;

      // Determine navigation type
      let navigationType: NavigationContext['type'] = 'button';
      
      if (link.closest('[data-sidebar]')) {
        navigationType = 'sidebar';
      } else if (link.closest('form')) {
        navigationType = 'form';
      }

      event.preventDefault();
      
      try {
        await healthyNavigate(href, undefined, navigationType);
      } catch (error) {
        console.error('Intercepted navigation failed:', error);
      }
    };

    // Intercept form submissions
    const handleSubmit = async (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      const action = form.getAttribute('action');
      
      if (!action || action.startsWith('http')) return;

      event.preventDefault();
      
      try {
        await healthyNavigate(action, undefined, 'form');
      } catch (error) {
        console.error('Form navigation failed:', error);
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('submit', handleSubmit);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('submit', handleSubmit);
    };
  }, [healthyNavigate]);

  // Cache warming for predicted routes
  useEffect(() => {
    const warmPredictedRoutes = async () => {
      const currentPath = location.pathname;
      const predictedRoutes: string[] = [];

      // Predict likely next routes based on current location
      if (currentPath === '/dashboard') {
        predictedRoutes.push('/companies', '/users', '/topics');
      } else if (currentPath.includes('/company/')) {
        predictedRoutes.push('/dashboard', '/topics');
      }

      if (predictedRoutes.length > 0) {
        await smartCacheManager.warmCache(predictedRoutes);
      }
    };

    const timer = setTimeout(warmPredictedRoutes, 1000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return {
    healthyNavigate,
    isNavigating: isNavigatingRef.current,
    lastNavigation: lastNavigationRef.current,
    healthStatus: navigationHealthService.getHealthTrend()
  };
};
